import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GitBranch,
  History,
  Trash2,
  UserCircle2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { api, ApiError } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

export function PositionDetailPage() {
  const { t } = useTranslation();
  const { positionId } = useParams<{ positionId: string }>();
  const qc = useQueryClient();
  const le = useLegalEntityId();

  const posQ = useQuery({
    queryKey: ["position", positionId],
    queryFn: () => api.getPosition(positionId!),
    enabled: !!positionId,
  });

  const historyQ = useQuery({
    queryKey: ["position-history", positionId],
    queryFn: () => api.positionAssignmentHistory(positionId!),
    enabled: !!positionId,
  });

  const reportsToQ = useQuery({
    queryKey: ["position-reports-to", positionId],
    queryFn: () => api.positionReportsTo(positionId!),
    enabled: !!positionId,
  });

  // Candidate positions for the reports-to picker — everything in the
  // LE except this position itself.
  const candidatesQ = useQuery({
    queryKey: ["positions", le],
    queryFn: () => api.listPositions(le),
    enabled: !!le,
  });

  const [editingReportsTo, setEditingReportsTo] = useState(false);
  const [reportsToError, setReportsToError] = useState<string | null>(null);

  const setReportsTo = useMutation({
    mutationFn: (targetId: string) =>
      api.setPositionReportsTo(positionId!, {
        reports_to_position_id: targetId,
      }),
    onSuccess: () => {
      setReportsToError(null);
      setEditingReportsTo(false);
      qc.invalidateQueries({ queryKey: ["position-reports-to", positionId] });
      qc.invalidateQueries({ queryKey: ["org-chart"] });
    },
    onError: (e: unknown) => {
      setReportsToError(
        e instanceof ApiError ? e.detail : String(e),
      );
    },
  });

  const abolish = useMutation({
    mutationFn: () => api.abolishPosition(positionId!),
    onSuccess: (p) => {
      qc.setQueryData(["position", positionId], p);
      qc.invalidateQueries({ queryKey: ["positions"] });
    },
  });

  if (posQ.isLoading) return <Loading />;
  if (posQ.error) return <ErrorPanel message={(posQ.error as Error).message} />;
  if (!posQ.data)
    return <ErrorPanel message={t("position_detail.position_not_found")} />;

  const p = posQ.data;
  const history = historyQ.data ?? [];
  const reportsTo = reportsToQ.data ?? [];
  const primaryEdge = reportsTo.find(
    (e) => e.hierarchy_type_code === "primary",
  );
  const otherEdges = reportsTo.filter(
    (e) => e.hierarchy_type_code !== "primary",
  );
  const candidates = (candidatesQ.data ?? []).filter(
    (c) => c.id !== p.id,
  );

  const patch = (patch: Record<string, unknown>) =>
    api.updatePosition(p.id, patch).then((updated) => {
      qc.setQueryData(["position", p.id], updated);
      qc.invalidateQueries({ queryKey: ["positions"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${p.position_number_display} · ${p.title_en}`}
        breadcrumbs={[
          { label: t("position_detail.back_to_positions"), href: "/people/positions" },
          { label: p.position_number_display },
        ]}
        actions={
          <Badge tone={p.status === "filled" ? "green" : "slate"}>
            {p.status}
          </Badge>
        }
      />

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("position_detail.section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("position_detail.title_en")}
            value={p.title_en}
            onSave={(v) => patch({ title_en: v })}
          />
          <EditableField
            label={t("position_detail.title_ar")}
            value={p.title_ar}
            rtl
            onSave={(v) => patch({ title_ar: v })}
          />
          <EditableField
            label={t("position_detail.description")}
            value={p.description}
            kind="textarea"
            onSave={(v) => patch({ description: v })}
          />
          <EditableField
            label={t("position_detail.position_type")}
            value={p.position_type}
            kind="select"
            options={[
              { value: "full_time", label: t("position_detail.type_full_time") },
              { value: "part_time", label: t("position_detail.type_part_time") },
              { value: "contract", label: t("position_detail.type_contract") },
              { value: "temporary", label: t("position_detail.type_temporary") },
            ]}
            onSave={(v) => patch({ position_type: v })}
          />
          <EditableField
            label={t("position_detail.fte")}
            value={p.full_time_equivalent}
            kind="number"
            onSave={(v) =>
              patch({ full_time_equivalent: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("position_detail.compensation_region")}
            value={p.compensation_region}
            kind="select"
            options={[
              { value: "west", label: t("position_detail.region_west") },
              { value: "central", label: t("position_detail.region_central") },
              { value: "east", label: t("position_detail.region_east") },
            ]}
            onSave={(v) => patch({ compensation_region: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("position_detail.section_flags")}
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <EditableField
            label={t("position_detail.is_saudized")}
            value={p.is_saudized}
            kind="boolean"
            onSave={(v) => patch({ is_saudized: v })}
          />
          <EditableField
            label={t("position_detail.allow_delegation")}
            value={p.allow_delegation}
            kind="boolean"
            onSave={(v) => patch({ allow_delegation: v })}
          />
          <EditableField
            label={t("position_detail.self_approver")}
            value={p.self_approver}
            kind="boolean"
            onSave={(v) => patch({ self_approver: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("position_detail.section_lifecycle")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("position_detail.activation_date")}
            value={p.activation_date}
            placeholder="YYYY-MM-DD"
            onSave={(v) => patch({ activation_date: v })}
          />
          <EditableField
            label={t("position_detail.retirement_date")}
            value={p.retirement_date}
            placeholder="YYYY-MM-DD"
            onSave={(v) => patch({ retirement_date: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("position_detail.section_relationships")}
        </h2>
        <dl className="grid gap-3 md:grid-cols-2 text-sm">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("position_detail.rel_job")}
            </dt>
            <dd className="mt-1">
              <Link
                to={`/people/jobs/${p.job_id}`}
                className="font-medium text-slate-900 hover:underline"
              >
                {p.job_code} · {p.job_title_en}
              </Link>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("position_detail.rel_department")}
            </dt>
            <dd className="mt-1 font-medium text-slate-900">
              {p.department_name_en}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("position_detail.rel_current_worker")}
            </dt>
            <dd className="mt-1">
              {p.current_assignee ? (
                <Link
                  to={`/people/employees/${p.current_assignee.id}`}
                  className="font-medium text-slate-900 hover:underline"
                >
                  {p.current_assignee.first_name_en}{" "}
                  {p.current_assignee.last_name_en} (
                  {p.current_assignee.employee_code})
                </Link>
              ) : (
                <span className="italic text-slate-400">
                  {t("position_detail.vacant")}
                </span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      {/* Reports-to — primary edge editable, dotted/matrix read-only */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
            <GitBranch className="h-4 w-4" />
            {t("position_detail.reports_to")}
          </h2>
          {!editingReportsTo && (
            <button
              type="button"
              onClick={() => {
                setReportsToError(null);
                setEditingReportsTo(true);
              }}
              className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              {primaryEdge
                ? t("position_detail.change")
                : t("position_detail.set_manager")}
            </button>
          )}
        </div>

        {editingReportsTo ? (
          <div className="flex flex-col gap-2">
            <select
              className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm"
              defaultValue={primaryEdge?.reports_to_position_id ?? ""}
              onChange={(e) => {
                if (e.target.value) setReportsTo.mutate(e.target.value);
              }}
            >
              <option value="">
                {t("position_detail.select_a_position")}
              </option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.position_number_display} — {c.title_en}
                </option>
              ))}
            </select>
            {reportsToError && (
              <p className="text-xs text-rose-700">{reportsToError}</p>
            )}
            <button
              type="button"
              onClick={() => {
                setEditingReportsTo(false);
                setReportsToError(null);
              }}
              className="w-fit text-xs text-slate-500 hover:text-slate-900"
            >
              {t("position_detail.cancel")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2 text-sm">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("position_detail.primary_manager")}
              </span>
              <div className="mt-1">
                {primaryEdge ? (
                  <Link
                    to={`/people/positions/${primaryEdge.reports_to_position_id}`}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {primaryEdge.reports_to_position_number_display} —{" "}
                    {primaryEdge.reports_to_title_en}
                  </Link>
                ) : (
                  <span className="italic text-slate-400">
                    {t("position_detail.no_reporting_line")}
                  </span>
                )}
              </div>
            </div>
            {otherEdges.map((edge) => (
              <div key={edge.hierarchy_type_code}>
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  {t("position_detail.manager_suffix", {
                    type: edge.hierarchy_type_name_en,
                  })}
                </span>
                <div className="mt-1">
                  <Link
                    to={`/people/positions/${edge.reports_to_position_id}`}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {edge.reports_to_position_number_display} —{" "}
                    {edge.reports_to_title_en}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Assignment history — every past + current holder */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          <History className="h-4 w-4" />
          {t("position_detail.assignment_history", { count: history.length })}
        </h2>
        {history.length === 0 ? (
          <p className="text-sm italic text-slate-500">
            {t("position_detail.no_one_held")}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className="text-start text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-2 py-2 text-start">
                  {t("position_detail.col_employee")}
                </th>
                <th className="px-2 py-2 text-start">
                  {t("position_detail.col_from")}
                </th>
                <th className="px-2 py-2 text-start">
                  {t("position_detail.col_to")}
                </th>
                <th className="px-2 py-2 text-start">
                  {t("position_detail.col_status")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="px-2 py-2 text-sm">
                    <Link
                      to={`/people/employees/${h.employee_id}`}
                      className="inline-flex items-center gap-1.5 font-medium text-slate-900 hover:underline"
                    >
                      <UserCircle2 className="h-4 w-4 text-slate-400" />
                      {h.employee_name_en}
                      <span className="text-xs text-slate-400">
                        ({h.employee_code})
                      </span>
                    </Link>
                  </td>
                  <td className="px-2 py-2 text-sm tabular-nums text-slate-700">
                    {h.assignment_start}
                  </td>
                  <td className="px-2 py-2 text-sm tabular-nums text-slate-700">
                    {h.assignment_end ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-sm">
                    {h.is_current ? (
                      <Badge tone="green">
                        {t("position_detail.badge_current")}
                      </Badge>
                    ) : (
                      <Badge tone="slate">
                        {t("position_detail.badge_past")}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {p.status !== "abolished" && (
        <section className="rounded-xl border border-rose-200 bg-rose-50 p-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-rose-700">
            {t("position_detail.danger_zone")}
          </h2>
          <p className="mb-3 text-sm text-rose-800">
            {t("position_detail.abolish_description")}
          </p>
          <button
            type="button"
            disabled={abolish.isPending || !!p.current_assignee}
            onClick={() => {
              if (!confirm(t("position_detail.abolish_confirm"))) return;
              abolish.mutate();
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            title={
              p.current_assignee
                ? t("position_detail.transfer_first")
                : ""
            }
          >
            <Trash2 className="h-4 w-4" />
            {abolish.isPending
              ? t("position_detail.abolishing")
              : t("position_detail.abolish_position")}
          </button>
        </section>
      )}
    </div>
  );
}
